import { Cached, KeyboardArrowDown } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  InputBase,
  Snackbar,
  Switch,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingAnimation from "../reusables/LoadingAnimation";
import { formatPhoneNumber, getPhoneOptionLabel } from "../utils/formatnumber";
import decryptApi from "../utils/decryptApi";
import encryptApi from "../utils/encryptApi";
import TwilioTrialBanner from "../reusables/TwilioTrialBanner";
import { checkAccountType, getOnTwilioTrial } from "../../utils/twilioTrial";

const MASKED_PLACEHOLDER = "•".repeat(32);

const TwilioIntegration = () => {
  const [org_data, setOrgData] = useState(
    JSON.parse(localStorage.getItem("organization"))
  );
  const [twilio_integration, setTwilioIntegration] = useState({
    account_sid: org_data.twilio_integration.M.account_sid.S,
    app_sid: org_data.twilio_integration?.M?.app_sid?.S,
    api_sid: org_data.twilio_integration?.M?.api_sid?.S,
    api_secret: org_data.twilio_integration?.M?.api_secret?.S,
    api_key: "",
    transcription_enabled:
      org_data.twilio_integration.M.transcription_enabled.BOOL,
    auto_configuration: org_data.twilio_integration.M.auto_configuration.BOOL,
    primary_phone: org_data.twilio_integration.M.primary_phone.S,
  });
  const [incomming_phonenumbers, setIncommingPhonenumbers] = useState(null);
  const [loader1, setLoader1] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errorMsg, setErrormsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  // When user is editing a secret field, we show what they're typing (masked); otherwise show 32 dots
  const [editingSecretField, setEditingSecretField] = useState(null); // "api_key" | "api_secret" | null
  const [editingSecretValue, setEditingSecretValue] = useState("");
  const [showTrialNotice, setShowTrialNotice] = useState(
    () => getOnTwilioTrial(org_data) === true
  );

  useEffect(() => {
    const fetchDecodedToken = async () => {
      try {
        const decodedApiKey = await decryptApi(
          org_data.twilio_integration.M.api_key.S
        );
        setTwilioIntegration((prev) => ({ ...prev, api_key: decodedApiKey }));
      } catch (error) {
        // Log when stored API key cannot be decrypted (corrupt or wrong key)
        console.error("Error decoding API key:", error);
      }
    };
    fetchDecodedToken();
  }, [org_data?.twilio_integration?.M?.api_key?.S]);

  async function getIncomingPhoneNumbers() {
    setLoader1(true);
    setErrormsg(null);

    await axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "twilio/get-incoming-phone-numbers",
        { organization_id: org_data.orgId.S }
      )
      .then((res) => {
        if (res.data.length < 1) {
          setErrormsg(
            "Please add an incoming phone number in your twilio account"
          );
          setLoader1(false);
        } else {
          setIncommingPhonenumbers(res.data);
          setLoader1(false);
        }
      })
      .catch((err) => {
        setLoader1(false);
      });
  }

  useEffect(() => {
    getIncomingPhoneNumbers();
  }, [twilio_integration.account_sid]);

  async function saveTwilioSettings() {
    setErrormsg(null);
    setLoader(true);

    const currentOrg = JSON.parse(localStorage.getItem("organization"));
    const twilioPayload = { ...twilio_integration };
    // Only send api_key when user has entered a new auth token in the form
    if (twilioPayload.api_key) {
      // keep in payload for backend to update
    } else {
      delete twilioPayload.api_key;
    }
    delete twilioPayload.api_secret;

    if (twilioPayload.primary_phone.length < 7) {
      setErrormsg("Please add a primary phone number");
      setLoader(false);
      return;
    }

    try {
      const check = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        { userId: JSON.parse(localStorage.getItem("user")).userId.S }
      );
      if (check.status !== 200) {
        setErrormsg("Unable to verify user.");
        setLoader(false);
        return;
      }
      const user___ = check.data.user;
      if (
        !user___.relatedOrgs.L?.find(
          (e) => e?.M?.org_id?.S === currentOrg.orgId.S
        )
      ) {
        window.location.reload();
        return;
      }

      const organization_data = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "update-organization",
        { orgId: currentOrg.orgId.S, twilio_integration: twilioPayload }
      );

      const updated_org = organization_data.data.organization;
      localStorage.setItem("organization", JSON.stringify(updated_org));
      setOrgData(updated_org);
      const ti = updated_org.twilio_integration?.M;
      setTwilioIntegration((prev) => ({
        ...prev,
        account_sid: ti?.account_sid?.S ?? prev.account_sid,
        app_sid: ti?.app_sid?.S ?? prev.app_sid,
        api_sid: ti?.api_sid?.S ?? prev.api_sid,
        transcription_enabled: ti?.transcription_enabled?.BOOL ?? prev.transcription_enabled,
        auto_configuration: ti?.auto_configuration?.BOOL ?? prev.auto_configuration,
        primary_phone: ti?.primary_phone?.S ?? prev.primary_phone,
        api_key: "",
        api_secret: "",
      }));
      setSuccessMsg("Twilio settings updated successfully");
      setTimeout(() => setSuccessMsg(null), 3000);

      const trialCheck = await checkAccountType(currentOrg.orgId.S);
      const refreshed = JSON.parse(localStorage.getItem("organization"));
      if (refreshed) {
        setOrgData(refreshed);
      }
      setShowTrialNotice(
        trialCheck.onTwilioTrial === true && !trialCheck.checkFailed
      );
    } catch (error) {
      if (error.response?.data?.message) {
        setErrormsg(error.response.data.message);
      } else {
        setErrormsg("Failed to update Twilio settings.");
      }
    }
    setLoader(false);
  }

  const generateApplicationDetails = async () => {
    setLoader(true);
    const authToken = twilio_integration.api_key || "";
    const twilioPayload = { ...twilio_integration };
    delete twilioPayload.api_secret;
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "twilio/generate-application-details",
        {
          account_sid: twilioPayload.account_sid,
          authToken: authToken || undefined,
          org_id: org_data.orgId.S,
          organization_name: org_data.organization_name?.S,
          org_timezone: org_data.org_timezone?.S,
          twilio_integration: twilioPayload,
        }
      );
      const org_ = response?.data?.organization;
      if (!org_?.twilio_integration?.M) {
        setErrormsg("Could not generate application keys. Please check your Twilio credentials and try again.");
        setLoader(false);
        return;
      }
      localStorage.setItem("organization", JSON.stringify(org_));
      setOrgData(org_);
      const integration = org_.twilio_integration.M;
      setTwilioIntegration((prev) => ({
        ...prev,
        account_sid: integration.account_sid?.S ?? "",
        app_sid: integration.app_sid?.S ?? "",
        api_sid: integration.api_sid?.S ?? "",
        transcription_enabled: integration.transcription_enabled?.BOOL ?? false,
        auto_configuration: integration.auto_configuration?.BOOL ?? false,
        primary_phone: integration.primary_phone?.S ?? "",
        api_key: "",
        api_secret: "",
      }));
      setSuccessMsg("Application keys generated successfully");
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      // Log for production diagnostics when app details generation fails
      console.error("TwilioIntegration: generate application details failed", err);
    }
    setLoader(false);
  };

  return (
    <div className="w-full min-w-0 max-w-full px-3 lg:px-0 py-4 lg:py-8">
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!successMsg}
        autoHideDuration={6000}
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrormsg(null)}
      >
        <Alert onClose={() => setErrormsg(null)} severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <div className="text-[14px] lg:text-[17px] font-bold mb-1">
        Twilio Integration
      </div>
      <div className="text-[12px] lg:text-[14px] text-[#6B7280] mb-5">
        Connect your Twilio account and configure phone numbers and options.
      </div>
      {successMsg && (
        <Alert color="success" sx={{ mb: 4 }}>
          <div className="text-black-600 text-[14px]">{successMsg}</div>
        </Alert>
      )}

      {showTrialNotice && <TwilioTrialBanner className="mb-4" />}

      {loader && (
        <div className="flex justify-center">
          <CircularProgress
            size={"1.2rem"}
            color="primary"
            sx={{ animationDuration: "0.7s" }}
          />
        </div>
      )}

      <div className="text-[12px] lg:text-[15px] font-bold mb-3">
        Twilio Settings
      </div>

      <div className="space-y-2 mb-3">
        <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
          Account SID
        </div>
        <InputBase
          value={twilio_integration.account_sid}
          onChange={(e) =>
            setTwilioIntegration((prev) => ({
              ...prev,
              account_sid: e.target.value,
            }))
          }
          sx={{
            bgcolor: "white",
            width: { lg: "100%", xs: "100%" },
            px: 2,
            fontSize: { lg: "15px", xs: "13px" },
            py: 0.5,
            border: "1px solid #D1D5DB",
            borderRadius: "4px",
          }}
        />
      </div>

      <div className="space-y-2 mb-3">
        <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
          Auth Token
        </div>
        <InputBase
          type="password"
          autoComplete="off"
          value={
            editingSecretField === "api_key"
              ? editingSecretValue
              : (twilio_integration.api_key || org_data?.twilio_integration?.M?.account_sid?.S)
                ? MASKED_PLACEHOLDER
                : ""
          }
          onChange={(e) => {
            if (editingSecretField === "api_key") {
              setEditingSecretValue(e.target.value);
              setTwilioIntegration((prev) => ({ ...prev, api_key: e.target.value }));
            }
          }}
          onFocus={() => {
            setEditingSecretField("api_key");
            setEditingSecretValue("");
          }}
          onBlur={() => setEditingSecretField(null)}
          placeholder="Enter auth token"
          sx={{
            bgcolor: "white",
            width: { lg: "100%", xs: "100%" },
            px: 2,
            fontSize: { lg: "15px", xs: "13px" },
            py: 0.5,
            border: "1px solid #D1D5DB",
            borderRadius: "2px",
          }}
        />
      </div>

      {org_data?.twilio_integration?.M?.app_sid?.S ? (
        <>
          <div className="space-y-2 mb-3">
            <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
              APP SID
            </div>
            <InputBase
              value={twilio_integration.app_sid}
              onChange={(e) =>
                setTwilioIntegration((prev) => ({
                  ...prev,
                  app_sid: e.target.value,
                }))
              }
              sx={{
                bgcolor: "white",
                width: { lg: "100%", xs: "100%" },
                px: 2,
                fontSize: { lg: "15px", xs: "13px" },
                py: 0.5,
                border: "1px solid #D1D5DB",
                borderRadius: "4px",
              }}
            />
          </div>
          <div className="space-y-2 mb-3">
            <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
              API SID
            </div>
            <InputBase
              value={twilio_integration.api_sid}
              onChange={(e) =>
                setTwilioIntegration((prev) => ({
                  ...prev,
                  api_sid: e.target.value,
                }))
              }
              sx={{
                bgcolor: "white",
                width: { lg: "100%", xs: "100%" },
                px: 2,
                fontSize: { lg: "15px", xs: "13px" },
                py: 0.5,
                border: "1px solid #D1D5DB",
                borderRadius: "4px",
              }}
            />
          </div>
          <div className="space-y-2 mb-3">
            <div className="lg:text-[14px] font-semibold text-[13px] text-[#374151]">
              API SECRET
            </div>
            <InputBase
              type="password"
              autoComplete="off"
              value={
                editingSecretField === "api_secret"
                  ? editingSecretValue
                  : (twilio_integration.api_secret || org_data?.twilio_integration?.M?.app_sid?.S)
                    ? MASKED_PLACEHOLDER
                    : ""
              }
              onChange={(e) => {
                if (editingSecretField === "api_secret") {
                  setEditingSecretValue(e.target.value);
                  setTwilioIntegration((prev) => ({
                    ...prev,
                    api_secret: e.target.value,
                  }));
                }
              }}
              onFocus={() => {
                setEditingSecretField("api_secret");
                setEditingSecretValue("");
              }}
              onBlur={() => setEditingSecretField(null)}
              placeholder="Enter API secret"
              sx={{
                bgcolor: "white",
                width: { lg: "100%", xs: "100%" },
                px: 2,
                fontSize: { lg: "15px", xs: "13px" },
                py: 0.5,
                border: "1px solid #D1D5DB",
                borderRadius: "4px",
              }}
            />
          </div>
          <div className="my-3">
            <p className="text-[13px] text-[#6B7280] mb-2">
              Having issues with browser calling?{" "}
              <span
                onClick={() => !loader && generateApplicationDetails()}
                className={loader ? "text-[#9CA3AF]" : "cursor-pointer text-brand-primary font-medium hover:underline"}
              >
                {loader ? "Regenerating…" : "Regenerate application keys"}
              </span>
            </p>
          </div>
        </>
      ) : (
        <div
          onClick={() => !loader && generateApplicationDetails()}
          className="cursor-pointer hover:bg-gray-50 text-brand-primary lg:w-[240px] w-full my-3 text-[14px] text-center border-brand-primary border-[1.5px] px-4 py-2 rounded-md"
        >
          {loader ? "loading ..." : "Generate Application Keys"}
        </div>
      )}

      <div className="space-y-1 mb-3">
        <div className="flex space-x-2 items-center">
          <div className="lg:text-[14px] font-semibold text-[13px]">
            Primary Phone Number
          </div>
        </div>
        <div className="lg:w-full w-full flex items-center space-x-2">
          <div className="flex flex-1">
            <Autocomplete
              value={
                incomming_phonenumbers?.find(
                  (n) => n.phoneNumber === twilio_integration.primary_phone
                ) || null
              }
              onChange={(e, newValue) =>
                setTwilioIntegration((prev) => ({
                  ...prev,
                  primary_phone: newValue ? newValue.phoneNumber : "",
                }))
              }
              options={incomming_phonenumbers || []}
              getOptionLabel={(option) => getPhoneOptionLabel(option)}
              renderInput={(params) => (
                <InputBase
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  placeholder="Select a phone number"
                  sx={{
                    bgcolor: "white",
                    width: "100%",
                    px: 2,
                    py: 1,
                    fontSize: "15px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  {getPhoneOptionLabel(option)}
                </li>
              )}
              popupIcon={<KeyboardArrowDown />}
              disableClearable
              fullWidth
              ListboxProps={{ style: { maxHeight: 260 } }}
            />
          </div>
          <div className="border rounded-[5px] py-[6px] relative bottom-1">
            {loader1 ? (
              <img
                src="/icons/loading-spinner.gif"
                className="w-[24px] cursor-pointer"
                alt=""
              />
            ) : (
              <Button
                sx={{
                  textTransform: "none",
                  bgcolor: "white",
                  color: "black",
                  py: "1px",
                  px: "10px",
                  borderRadius: "7px",
                  fontSize: { lg: "14px", xs: "13px" },
                }}
                onClick={getIncomingPhoneNumbers}
                disabled={loader1}
              >
                {loader1 ? (
                  <CircularProgress size="1rem" sx={{ color: "black" }} />
                ) : (
                  <Cached size="1rem" sx={{ color: "black" }} />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex my-2 items-center gap-2 mb-16 lg:mb-0">
        <span className="lg:text-[14px] text-[13px] text-gray-700">
          Twilio Autoconfiguration
        </span>
        <Tooltip
          title="Twilio Autoconfiguration is a setting where we will automatically update your Twilio numbers to point to call routes set up in Twilanswer. It is recommended to be enabled"
          arrow
        >
          <img src="/icons/tooltip.png" className="w-[15px] lg:ml-1" alt="" />
        </Tooltip>
        <Switch
          color="primary"
          checked={twilio_integration.auto_configuration}
          onChange={(e) =>
            setTwilioIntegration((prev) => ({
              ...prev,
              auto_configuration: e.target.checked,
            }))
          }
          size="small"
        />
      </div>

      <div className="flex justify-end pt-5 mt-4">
        <Button
          variant="contained"
            color="primary"
            sx={{
            py: { lg: "10px", xs: "5px" },
            px: "20px",
            borderRadius: "12px",
            fontSize: { lg: "14px", xs: "12px" },
            height: "fit",
          }}
          onClick={saveTwilioSettings}
          disabled={loader1 || loader}
        >
          {loader ? (
            <CircularProgress size="1rem" sx={{ color: "white" }} />
          ) : (
            " Save Changes"
          )}
        </Button>
      </div>
    </div>
  );
};

export default TwilioIntegration;
